import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_BASE_URL } from '../../config';
import './CreateGroupRide.css';

interface ClubOption {
  id: number;
  name: string;
  short_name: string;
}

interface CreateGroupRideProps {
  onBack: () => void;
  onRideCreated?: (rideId: number) => void;
}

const CreateGroupRide: React.FC<CreateGroupRideProps> = ({ onBack, onRideCreated }) => {
  const { t } = useLanguage();

  // Form state
  const [clubs, setClubs] = useState<ClubOption[]>([]);
  const [clubId, setClubId] = useState<number | ''>('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [startLocation, setStartLocation] = useState('');
  const [endLocation, setEndLocation] = useState('');
  const [rideType, setRideType] = useState('road');
  const [pace, setPace] = useState('no_drop');

  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchClubs();
  }, []);

  const fetchClubs = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/clubs`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        const clubList = (data.clubs || []).map((c: any) => ({
          id: c.id,
          name: c.name,
          short_name: c.short_name,
        }));
        setClubs(clubList);
        if (clubList.length === 1) {
          setClubId(clubList[0].id);
        }
      }
    } catch {
      // ignore
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const body: any = {
        title: title.trim(),
        start_time: new Date(startTime).toISOString(),
        ride_type: rideType,
        group_pace: pace,
      };

      if (endTime) {
        body.end_time = new Date(endTime).toISOString();
      }
      if (clubId) {
        body.club_id = clubId;
      }
      if (description.trim()) {
        body.description = description.trim();
      }
      if (startLocation.trim()) {
        body.start_location_name = startLocation.trim();
      }
      if (endLocation.trim()) {
        body.end_location_name = endLocation.trim();
      }

      const res = await fetch(`${API_BASE_URL}/api/group-rides`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (res.ok) {
        const data = await res.json();
        if (onRideCreated && data.group_ride?.id) {
          onRideCreated(data.group_ride.id);
          return; // Skip form reset — navigating away
        }
        setSuccess(true);
        // Reset form
        setTitle('');
        setDescription('');
        setStartTime('');
        setEndTime('');
        setStartLocation('');
        setEndLocation('');
        setRideType('road');
        setPace('no_drop');
        setTimeout(() => setSuccess(false), 4000);
      } else {
        const data = await res.json();
        setError(data.error || t('createGroupRide.error'));
      }
    } catch {
      setError(t('createGroupRide.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="create-group-ride">
      <div className="create-group-ride-header">
        <button onClick={onBack} className="create-group-ride-back">
          ← {t('common.back')}
        </button>
        <h2>{t('createGroupRide.title')}</h2>
      </div>

      {success && (
        <div className="create-group-ride-success">
          {t('createGroupRide.success')}
        </div>
      )}

      {error && (
        <div className="create-group-ride-error">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="create-group-ride-form">
        {/* Club Picker - only if multiple clubs */}
        {clubs.length > 1 && (
          <div className="form-group">
            <label>{t('createGroupRide.club')}</label>
            <select
              value={clubId}
              onChange={(e) => setClubId(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">{t('createGroupRide.selectClub')}</option>
              {clubs.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Title */}
        <div className="form-group">
          <label>{t('createGroupRide.rideTitle')} *</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            placeholder={t('createGroupRide.rideTitlePlaceholder')}
          />
        </div>

        {/* Description */}
        <div className="form-group">
          <label>{t('createGroupRide.description')}</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder={t('createGroupRide.descriptionPlaceholder')}
          />
        </div>

        {/* Start & End Time */}
        <div className="form-row">
          <div className="form-group">
            <label>{t('createGroupRide.startTime')} *</label>
            <input
              type="datetime-local"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('createGroupRide.endTime')} *</label>
            <input
              type="datetime-local"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              required
            />
          </div>
        </div>

        {/* Start & End Location */}
        <div className="form-row">
          <div className="form-group">
            <label>{t('createGroupRide.startLocation')}</label>
            <input
              type="text"
              value={startLocation}
              onChange={(e) => setStartLocation(e.target.value)}
              placeholder={t('createGroupRide.locationPlaceholder')}
            />
          </div>
          <div className="form-group">
            <label>{t('createGroupRide.endLocation')}</label>
            <input
              type="text"
              value={endLocation}
              onChange={(e) => setEndLocation(e.target.value)}
              placeholder={t('createGroupRide.locationPlaceholder')}
            />
          </div>
        </div>

        {/* Ride Type */}
        <div className="form-group">
          <label>{t('createGroupRide.rideType')}</label>
          <div className="radio-group">
            {['road', 'gravel', 'training'].map((type) => (
              <label key={type} className="radio-label">
                <input
                  type="radio"
                  name="rideType"
                  value={type}
                  checked={rideType === type}
                  onChange={(e) => setRideType(e.target.value)}
                />
                <span>{t(`createGroupRide.${type}`)}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Pace */}
        <div className="form-group">
          <label>{t('createGroupRide.pace')}</label>
          <div className="radio-group">
            <label className="radio-label">
              <input
                type="radio"
                name="pace"
                value="no_drop"
                checked={pace === 'no_drop'}
                onChange={(e) => setPace(e.target.value)}
              />
              <span>{t('createGroupRide.noDrop')}</span>
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="pace"
                value="drop"
                checked={pace === 'drop'}
                onChange={(e) => setPace(e.target.value)}
              />
              <span>{t('createGroupRide.drop')}</span>
            </label>
          </div>
        </div>

        {/* Submit */}
        <button
          type="submit"
          className="create-group-ride-submit"
          disabled={submitting || !title.trim() || !startTime || !endTime}
        >
          {submitting ? t('createGroupRide.creating') : t('createGroupRide.create')}
        </button>
      </form>
    </div>
  );
};

export default CreateGroupRide;
